/* eslint-disable no-param-reassign */
import {
  useCallback, useContext, useEffect, useState, useRef,
} from 'react';
import {
  useParams, useHistory,
} from 'react-router-dom';
import { useFullScreenHandle } from 'react-full-screen';
import { useTheme } from 'styled-components';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';

// components
import { useReactToPrint } from 'react-to-print';
import GridKpis from '../../components/GridKpis';
import useGridKpi from '../../hooks/useGridKpi';
import GridKpisToolbar from '../../components/Toolbars/GridKpisToolbar';
import Mural from '../../components/Mural';
import VSResumeDrawer from '../../components/VSResumeDrawer';
import ResumeKpiView from '../../components/Modals/ResumeKpiView';
import ShareList, { PublicLinkAlert } from '../../components/Modals/ShareList';
import CreateTemplate, { UploadModel } from '../../components/ProgressiveModals/CreateTemplate';
import FileValidation from '../../components/Modals/FileValidation';
import DashboardDependencies from '../../components/Modals/DashboardDependencies';
import NoKpi from '../../juristec-ui/kpis/grid/NoKpi';
import Loader from '../../juristec-ui/core/Loader';
import GlobalFiltersColumns from '../../components/Modals/GlobalFiltersColumns';
import IframeCreate from '../../components/Modals/IframeCreate';
import SubsetTableCreate from '../../components/Modals/SubsetTableCreate';
import TitleModal from '../../components/Modals/SubsetTableCreate/TitleModal';
import FileReport from '../../components/FilesView/FileReport';
import FileVisualize from '../../components/Modals/FileVisualize';
import FileUpload from '../../components/Modals/FileUpload';
import DashGridSize from '../../components/Modals/DashGridSize';
import QuickDash from '../../components/Modals/QuickDash';
import TransferKpi, { ResponseList } from '../../components/Modals/TransferKpi';

import ReportGridEditor from '../../components/ReportGrid/ReportGridEditor';
import FileConfigForm from '../../components/ReportGrid/FileConfigForm';
import ReportGridFilters from '../../components/ReportGrid/ReportGridFilters';
import ReportGridDetails from '../../components/ReportGrid/ReportGridDetails';
import useReportDefinitions from '../../components/ReportGrid/useReportDefinitions';

// hooks
import useDeviceType from '../../juristec-ui/hooks/useDeviceType';

// functions & others
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';
import { TourContext } from '../../context/TourProvider';
import { AuthContext } from '../../context/AuthProvider';
import { UsersContext } from '../../context/UsersProvider';
import { GroupsContext } from '../../context/GroupsProvider';
import { ModalContext } from '../../context/ModalProvider';
import { AlertContext } from '../../context/AlertProvider';
import { FilesContext } from '../../context/FilesProvider';
import { TemplateContext } from '../../context/TemplateProvider';
// import { AlertContext, ModalContext, Loader } from '../../components/Juristec/core';
import useToggleState from '../../juristec-ui/hooks/useToggleState';
import useGridDashboard from '../../hooks/useGridDashboard';
import useKpiData from '../../hooks/useKpiData';
import useLocalStorage from '../../hooks/useLocalStorage';
import urlHash from '../../utils/functions/urlHash';
import renameFile from '../../utils/functions/renameFile';
import { shareDashOptions } from '../../options';
import { error } from '../../label';

import { verifyFileImgType, verifyFileSize } from '../../juristec-ui/utils/validators/fileValidators';
import getDefaultStyle from '../../juristec-ui/kpis/utils/chartTools/getDefaultStyle';
// import { size } from '../../juristec-ui/core/breakpoints';

// import {
//   InputWrapper,
// } from './styled/SubsetTableCreate.styled';

const printLoadMsg = 'Preparando a impressão. Esta tarefa pode levar alguns segundos...';

const getType = (typeLabel) => {
  switch (typeLabel) {
    case 'date':
      return 'datetime64[ns]';
    case 'float':
      return 'float64';
    default:
      return 'category';
  }
};

export default function KpisPage() {
  const history = useHistory();
  const { hash } = useParams();
  const theme = useTheme();

  const getParams = () => {
    const str = urlHash(hash, true).split('/');
    return { dashboardKey: str[0], userId: str[1] };
  };
  const { dashboardKey, userId } = getParams();
  const {
    currentUser, userCompany, user, claimsUser, plans, gptUsedTokens, gptBonusTokens,
  } = useContext(AuthContext);
  const { state: usersState } = useContext(UsersContext);
  const { state: groupsState, groupsAPI } = useContext(GroupsContext);
  const { state: filesState, filesAPI } = useContext(FilesContext);
  const totalSize = parseFloat(userCompany?.volumeData || 0);
  const { closeModal, setModalConfig } = useContext(ModalContext);
  const { setAlertConfig } = useContext(AlertContext);
  const { state: templateState, templateAPI } = useContext(TemplateContext);

  const gridKpiRef = useRef();
  const {
    tourOpen, nextStep, refreshTour, remakeTourList,
  } = useContext(TourContext);
  const handle = useFullScreenHandle();

  const [kpiDataAPI] = useKpiData(currentUser);

  const [gridSizeLocal, setGridSizeLocal] = useLocalStorage(`${dashboardKey}-${user.id}-size`, null);
  const [gridSize, setGridSize] = useState(null);

  const device = useDeviceType();

  const isOwner = userId === currentUser.uid;
  const dashboardKeyValid = dashboardKey.split('_')[1] || dashboardKey;
  // const dashboardKeyValid = isOwner ? dashboardKey : dashboardKey.split('_')[1];

  const [gridKpiState, gridKpiAPI] = useGridKpi(userId, dashboardKeyValid, currentUser, user);
  const [editPainel, setEditPainel] = useState(null);
  const [openMural, setOpenMural] = useState(false);
  const [VSDrawer, toggleVSDrawer, closeVSDrawer] = useToggleState(false);
  const [gridBreakpoint, setGridBreakpoint] = useState('lg');
  const [showToolbar, setShowToolbar] = useState(true);

  const [multiSelection, setMultiSelection] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Map());

  const [showToPrint, setShowToPrint] = useState(false);
  const [manualLoad, setManualLoad] = useState(false);

  const [showQuickDash, setShowQuickDash] = useState(false);

  const [dashFiles, setDashFiles] = useState([]);
  const [globalFilterSelectors, setGlobalFilterSelectors] = useState({});
  const [globalFilterLimits, setGlobalFilterLimits] = useState({});
  const globalFilters = useRef({});
  const globalFiltersOrder = useRef([]);
  const dataToFilter = useRef({});
  const cron = useRef();
  const detachDash = useRef();
  // const dashLoaded = useRef(false);

  const [reportDefinitions, setReportDefinitions, retryReportGrid] = useReportDefinitions(
    gridKpiState.kpiItemList,
    kpiDataAPI,
    JSON.stringify([currentUser.uid, userId, dashboardKeyValid, gridKpiState.collection]),
  );

  const handleReportDensityChange = useCallback((id, density) => {
    setReportDefinitions((previous) => {
      const definition = previous[id];
      if (!definition || definition.settings.density === density) return previous;

      return {
        ...previous,
        [id]: {
          ...definition,
          settings: { ...definition.settings, density },
        },
      };
    });
  }, []);

  const {
    dashboardDoc,
    kpiItemList,
    kpisLayout,
    collection,
    isLoading,
  } = gridKpiState;

  // useEffect(() => {
  //   gridKpiAPI.loadCardsFromCollection();
  // }, [gridKpiAPI]);

  const loadDash = async (ignoreDetach = false) => {
    if (!detachDash.current || ignoreDetach) {
      const result = await gridKpiAPI.loadLiveCardsFromCollection();
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
      } else {
        console.log('LISTENER IN');
        detachDash.current = () => {
          console.log('LISTENER OUT');
          result.detachDash();
          gridKpiAPI.dashLoaded.current = false;
          detachDash.current = undefined;
        };
      }
    }
  };

  useEffect(() => {
    loadDash(true);
    return () => {
      detachDash.current?.();
    };
  }, []);

  useEffect(() => {
    if (dashboardDoc?.isDeleted) {
      setAlertConfig({
        type: 'error',
        text: 'Dashboard removido ou não encontrado!',
        raw: '',
      });
    }
  }, [dashboardDoc?.isDeleted]);

  useEffect(() => {
    if (gridSizeLocal === null) setGridSize(kpisLayout.size || 'auto');
    else setGridSize(gridSizeLocal);
  }, [kpisLayout?.size]);

  useEffect(() => {
    if (dashboardDoc?.globalFilters && dashFiles.length > 0) {
      const auxGFS = dashboardDoc.globalFilters?.reduce((aux, gf) => {
        const uid = gf.uid || uuidv4();
        aux[uid] = {
          ...gf,
          uid,
          selector: gf.type === 'datetime64[ns]' ? 'year' : '',
          filename: dashFiles.find((ff) => ff.file_id === gf.database)?.filename || '',
          isOpen: false,
        };
        return aux;
      }, {});
      setGlobalFilterSelectors(auxGFS);
      if (dashboardDoc?.lastChangesAffect === 'filters') {
        globalFilters.current = {};
      } else if (dashboardDoc?.lastChangesAffect === 'kpis') {
        // Força a atualização do ref para refazer a chamada dos filtros
        const aux = cloneDeep(globalFilters.current);
        globalFilters.current = {};
        globalFilters.current = aux;
      }
    }
    if (dashboardDoc?.globalLimits && dashFiles.length > 0) {
      setGlobalFilterLimits(dashboardDoc.globalLimits?.reduce((aux, gl) => {
        const uid = gl.uid || uuidv4();
        aux[gl.database + gl.column] = {
          ...gl,
          uid,
          values: gl.values,
          filename: dashFiles.find((ff) => ff.file_id === gl.database)?.filename || '',
        };
        return aux;
      }, {}));
    }
  }, [dashboardDoc?.globalFilters, dashboardDoc?.globalLimits, dashFiles.length]);

  useEffect(() => {
    if (!templateState.templates.length > 0 && !templateState.isLoading) {
      (async () => {
        await templateAPI.getTemplates();
      })();
    }
    if (!filesState.started && !filesState.fileLoading) {
      (async () => {
        await filesAPI.init();
      })();
    }
    if (!groupsState.isLoading && !groupsState.started) {
      (async () => {
        await groupsAPI.getGroups();
      })();
    }
  }, [
    filesState.started,
    filesAPI,
    filesState.fileLoading,
    groupsState.started,
    groupsAPI,
    groupsState.isLoading,
  ]);

  useEffect(() => {
    if ((
      filesState?.files?.length > 0
      || filesState?.instanceFiles?.length > 0
    ) && dashboardDoc?.databases) {
      setDashFiles(filesState.instanceFiles.reduce((aux, fileInfo) => {
        if (dashboardDoc.databases.includes(fileInfo.file_id)) {
          const file = filesState.files.find((fullFile) => fullFile.file_id === fileInfo.file_id);
          if (file) {
            aux.push({
              ...file,
              label: file.filename,
              value: file.file_id,
              id: file.file_id,
              allowed: true,
            });
          } else {
            aux.push({
              ...fileInfo,
              allowed: false,
            });
          }
        }
        return aux;
      }, []));
    }
  }, [filesState.files, filesState.instanceFiles, dashboardDoc?.databases]);

  const closeMural = () => setOpenMural(false);

  const changeCurrentSnap = async (snapId) => {
    if (snapId === collection) return;
    detachDash.current?.();
    if (snapId === 'kpis') loadDash();
    else {
      const result = await gridKpiAPI.loadCardsFromCollection(snapId);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
      }
    }
  };

  function snapshotReachedtLimit(snaps) {
    const plan = userCompany?.plan || 'advanced';
    const companyPlan = plans[plan];
    const limit = +(userCompany?.snapshots || companyPlan?.snapshots);
    if (!limit) throw new Error('Quantidade de snapshots não numerico');
    return snaps.length >= limit;
  }

  const subsetTableReachedLimit = () => {
    const subsetTablesInDash = kpiItemList.filter((k) => k.type === 'SubsetTable');
    const companyPlan = plans[userCompany?.plan || 'advanced'];
    const subsetTableLimit = userCompany?.subsetTableLimit ?? companyPlan.subsetTableLimit;
    return subsetTablesInDash.length >= subsetTableLimit;
  };

  const handleCreateSnapshot = async (snapName) => {
    try {
      const oldsSnaps = dashboardDoc.snapshots || [];
      const plan = userCompany.plan || 'advanced';
      if (snapshotReachedtLimit(oldsSnaps)) {
        switch (plan) {
          case 'premium':
            setAlertConfig({
              type: 'error',
              text: 'Limite de Stories alcançados!',
              child: 'Para incluir um novo story você deve apagar algum dos existentes para liberar espaço.',
            });
            break;
          default:
            setAlertConfig({
              type: 'error',
              text: 'Limite de Stories alcançados!',
              child: `Seu plano permite apenas ${userCompany?.snapshots ?? plans[userCompany.plan].snapshots} Stories. Para incluir um novo story você deve apagar algum dos existentes para liberar espaço ou fazer um upgrade de plano.`,
            });
            break;
        }
        return;
      }
      const result = await gridKpiAPI
        .saveSnapshot(snapName, oldsSnaps, kpiItemList, kpisLayout);
      // (snapName, oldSnaps, cardList, cardsLayout, deleteOlder);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Story criado com sucesso!',
      });
      if (tourOpen) nextStep();
      closeModal();
    } catch (er) {
      setAlertConfig({
        type: 'error',
        text: 'Algo deu errado!',
        // child: `${result.raw}`,
      });
    }
  };

  const handleScheduleSnapshot = async (cronCode, snapName) => {
    const plan = userCompany?.plan || 'advanced';
    const scheduledSnapshotsAllowed = (
      userCompany?.scheduledSnapshotAllowed
      ?? plans[plan].scheduledSnapshotAllowed
    );
    if (!scheduledSnapshotsAllowed) {
      setAlertConfig({
        type: 'error',
        text: 'Você não tem permissão para agendar Stories!',
        child: 'Sua instância não possui permissão para agendamento de Stories',
      });
      return;
    }
    const snaps = dashboardDoc.snapshots || [];
    if (snapshotReachedtLimit(snaps)) {
      switch (userCompany.plan) {
        case 'premium':
          setAlertConfig({
            type: 'error',
            text: 'Limite de Stories alcançados!',
            child: 'Para incluir um novo story você deve apagar algum dos existentes para liberar espaço.',
          });
          break;
        default:
          setAlertConfig({
            type: 'error',
            text: 'Limite de Stories alcançados!',
            child: `Seu plano permite apenas ${userCompany?.snapshots ?? plans[userCompany.plan].snapshots} Stories. Para incluir um novo story você deve apagar algum dos existentes para liberar espaço ou fazer um upgrade de plano.`,
          });
          break;
      }
      return;
    }

    const result = await gridKpiAPI.scheduleSnapshot(cronCode, snapName);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Story agendado com sucesso!',
    });
    if (tourOpen) nextStep();
    closeModal();
  };

  // editar o nome do snapshot
  const handleEditSnapshot = async (name, snapId) => {
    const oldSnaps = dashboardDoc.snapshots;
    const result = await gridKpiAPI.editSnapshot({ name, snapId }, oldSnaps);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Story editado com sucesso!',
    });
    closeModal();
  };

  // deletar o nome do snapshot
  const handleDeleteSnapshot = (snap) => {
    const deleteSnapshot = async () => {
      const oldSnaps = dashboardDoc.snapshots;
      const result = await gridKpiAPI.removeSnapshot(snap, oldSnaps);
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: 'Story apagado com sucesso!',
      });

      // se deletar no qual está selecionado, volta para o 'kpis'
      if (collection === snap.snapId) {
        await changeCurrentSnap();
      }
      closeModal();
    };

    setAlertConfig({
      type: 'warning',
      text: 'Tem certeza que deseja excluir o story?',
      child: (
        <span>
          O story
          {' '}
          <strong style={{ fontStyle: 'italic' }}>
            {snap.name}
          </strong>
          {' '}
          não poderá ser recuperado!
        </span>
      ),
      withoutConfirm: true, // tirar o ok do alerta
      withFunction: true, // colocar btn cancelar e confirmar
      confirmFunction: deleteSnapshot,
    });
  };

  const handleCardStyles = async (cardStyles) => {
    const result = await gridKpiAPI.setCardStyles(cardStyles);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }

    setAlertConfig({
      type: 'success',
      text: 'Estilo editado com sucesso!',
    });
    closeModal();
  };

  const handleSaveLayout = async (newLayout) => {
    const result = await gridKpiAPI.saveLayout(newLayout);
    if (result.error) setAlertConfig({ type: 'error', text: 'Erro ao salvar o layout' });
  };

  const [{ isLoading: gridAPILoading, dashboards }, gridAPI] = useGridDashboard(currentUser, user);

  const handleShare = () => {
    const companyPlan = plans[userCompany.plan];
    const genSuccessMsg = (dashName, sharedUsers = [], missing) => {
      let child = '';
      let type = 'success';
      if (sharedUsers.length > 0) {
        child = `O dashboard ${dashName} está compartilhado com ${sharedUsers.map((u) => (
          `${u.name} (${shareDashOptions.get(u.sharePermission?.value)})`)).join(', ')}.`;
        if (missing?.deleted?.length > 0 || missing?.noPermission?.length > 0) {
          type = 'warning';
          child += " Porém alguns KPI's dependem de";
          if (missing.deleted.length > 0) {
            child += ` ${missing.deleted.length} arquivos não encontrados${missing.noPermission.length > 0 ? ' e' : '.'}`;
          }
          if (missing.noPermission.length > 0) child += ` ${missing.deleted.length} arquivos que não são seus.`;
        }
        child = (
          <>
            <div>
              {child}
            </div>
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: theme.warning, fontWeight: 'bold' }}>
                Atenção:
              </span>
              {' '}
              <span>
                Os arquivos dependentes utilizados no dashboard também serão compartilhados
                para os usuários cientistas e proprietários selecionados,
                com permissão de Visualização.
              </span>
            </div>
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: theme.warning, fontWeight: 'bold' }}>
                Atenção 2:
              </span>
              {' '}
              <span>
                Filtros globais e variáveis de controle podem não estar disponíveis para os usuários
                compartilhados caso você não seja o proprietário dos arquivos dependentes.
              </span>
            </div>
          </>
        );
      } else {
        child = `O dashboard ${dashName} não está compartilhado com outros usuários.`;
      }
      return {
        text: 'Opções de compartilhamento atualizadas!',
        type,
        child,
      };
    };

    const submitShare = async (selectedUsers, selectedGroups, removed) => {
      const result = await gridAPI.shareDashboard(
        gridKpiState.dashboardDoc,
        usersState.users,
        selectedUsers,
        selectedGroups,
        removed,
        filesState.files,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }
      // gridKpiAPI.loadCardsFromCollection();
      loadDash();
      // await gridKpiAPI.setDashInfo(gridKpiState.dashboardDoc); // atualizar o doc //
      setAlertConfig(genSuccessMsg(
        gridKpiState.dashboardDoc.name, [...selectedUsers, ...selectedGroups], result.missing,
      ));
      closeModal(); // aqui...
    };

    const genPublicUrl = async (operation, publicUrl, pwd, expDt) => {
      const res = await gridKpiAPI.genPublicKpis(
        { ...dashboardDoc, publicUrl: publicUrl ?? dashboardDoc.publicUrl },
        operation,
        pwd,
        expDt,
      );
      const isDel = operation === 'delete';
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: `Não foi possível ${isDel ? 'desativar o link público' : 'gerar cópia pública'}. Tente novamente mais tarde`,
        });
      } else {
        setAlertConfig({
          type: 'success',
          text: `Link público ${isDel ? 'desativado' : 'atualizado'} com sucesso.`,
          child: !isDel ? <PublicLinkAlert /> : null,
        });
        // gridKpiAPI.loadCardsFromCollection();
        loadDash();
      }
      return res;
    };
    setModalConfig({
      title: 'Compartilhar Dashboard',
      close: closeModal,
      children: (
        <ShareList
          users={usersState.users}
          groups={groupsState.groups}
          owner={gridKpiState?.dashboardDoc.owner}
          publicLink={userCompany.publicLink}
          sharedWith={gridKpiState?.dashboardDoc?.sharedWith}
          submit={submitShare}
          close={closeModal}
          publicUrl={dashboardDoc.publicUrl}
          publicTimestamp={dashboardDoc.publicTimestamp}
          genPublicUrl={genPublicUrl}
          permissionsOpts={shareDashOptions.getOptions()}
          expiresAt={dashboardDoc.publicExpiration}
          passwordProtected={dashboardDoc?.passwordProtected}
          getAllInfos={gridKpiAPI.getDashInfo}
          permissionsPlan={{
            daysToExpire: (
              userCompany.publicLinkExpirationDays ?? companyPlan.publicLinkExpirationDays
            ),
            password: userCompany.publicLinkPwd ?? companyPlan.publicLinkPwd,
          }}
        />
      ),
    });
  };

  const handleCreateTemplate = async () => {
    const handleSubmitValidation = async (validation) => {
      const uploadedRes = await filesAPI.uploadTemplateFileModel(validation);
      if (uploadedRes.error) {
        setAlertConfig({
          type: 'error',
          text: uploadedRes.msg,
          child: uploadedRes.raw,
        });
        return false;
      }
      return true;
    };

    const handleValidation = (fileInfos, fileId, submitData) => {
      if (!fileInfos) return;

      const handleSubmit = async ({
        dateFormat,
        floatColumns,
        dateColumns,
        selectedDecimal,
        startLine,
      }) => {
        const newFileId = fileId ?? `FILEID${uuidv4().replace(/-/g, '')}`;
        const validation = {
          file_id: newFileId,
          filename: fileInfos.name || fileInfos.filename,
          uploaded_file: fileInfos.name || fileInfos.filename,
          date_columns: dateColumns,
          float_columns: floatColumns,
          int_columns: [],
          decimal: selectedDecimal || ',',
          date_format: dateFormat || '',
          header: startLine || 0,
          overwrite: true,
        };
        closeModal('validation_modal');
        closeModal('upload_model_modal');
        submitData(newFileId, fileInfos.name || fileInfos.filename, validation);
      };
      setModalConfig({
        nodeTarget: 'validation_modal',
        title: 'Validação de dados do Arquivo Modelo',
        className: 'modal_validation',
        children: (
          <FileValidation
            hide={closeModal}
            fileData={fileInfos}
            filename={fileInfos.name || fileInfos.filename}
            submitData={handleSubmit}
            reopenFile={filesAPI.reopen}
          />
        ),
      });
    };

    const uploadFileModel = async (file) => {
      const fileRes = await filesAPI.uploadFile(file, true);
      if (fileRes.error) {
        setAlertConfig({ type: 'error', text: `${fileRes.msg}`, child: fileRes.raw });
        return null;
      }
      return fileRes.res;
    };

    const submitNewTemplate = async (template) => {
      const cardStyles = dashboardDoc.cardStyles || {};
      const result = await gridKpiAPI.createTemplate(
        { ...template, cardStyles }, kpiItemList, kpisLayout,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return false;
      }
      return true;
    };

    const handleSubmit = async (data, validation) => {
      const editSuccess = await submitNewTemplate(data);
      if (editSuccess) {
        if (validation) await handleSubmitValidation(validation);
        setAlertConfig({
          type: 'success',
          text: 'Template criado com sucesso!',
        });
        closeModal('new_template_modal');
      }
    };

    // Function to check if the image satisfies the specified conditions
    const checkFileIsImg = (file) => {
      const errorMsg = verifyFileImgType(file) || verifyFileSize(file, 1000000);
      if (errorMsg.length > 0) {
        setAlertConfig({ type: 'error', text: errorMsg });
        return false;
      }
      return true;
    };

    const templateCategories = Object.values(templateState.templates?.reduce((aux, t) => {
      if (t.category) {
        aux[t.category] = { label: t.category, value: t.category, id: `category-${t.category}` };
      }
      return aux;
    }, {}) || {});

    const columnsRequired = await gridKpiAPI.getColumnDependecies(
      kpiItemList, dashboardDoc?.globalFilters,
    );
    // const aux = Object.keys(columnsRequired).reduce((acc, cur) => {
    //   acc[cur] = { type: columnsRequired[cur], description: '' };
    //   return acc;
    // }, {});

    const openUploadModal = (fileId, callback) => {
      const submitFile = async (file) => {
        const fileInfos = await uploadFileModel(file);
        if (fileInfos) {
          handleValidation(
            fileInfos,
            fileId,
            callback,
          );
        }
      };

      setModalConfig({
        nodeTarget: 'upload_model_modal',
        title: 'Upload do Arquivo Modelo',
        className: 'upload_model_modal',
        children: (
          <UploadModel
            hide={() => closeModal('upload_model_modal')}
            submit={submitFile}
          />
        ),
      });
    };

    setModalConfig({
      nodeTarget: 'new_template_modal',
      title: 'Criar Template',
      children: (
        <CreateTemplate
          hide={closeModal}
          submitData={handleSubmit}
          templateVars={columnsRequired}
          categories={templateCategories}
          checkImage={checkFileIsImg}
          openUploadModal={openUploadModal}
        />
      ),
    });
  };

  const redirectToKpiCreation = () => {
    history.push(`/kpi/${urlHash(`${dashboardKey}/new`)}`);
  };

  const redirectToInsightCreation = () => {
    history.push(`/label/${urlHash(`${dashboardKeyValid}/new`)}`);
  };

  const setGridRef = useCallback((node) => {
    gridKpiRef.current = node;
  }, []);

  const handleBeforePrint = async () => {
    setManualLoad('print');
    return new Promise((resolve) => {
      setTimeout(() => {
        setShowToPrint(true);
        setTimeout(() => {
          setManualLoad(false);
          resolve();
        }, 5000);
      }, 1000);
    });
  };

  const handleAfterPrint = () => {
    setShowToPrint(false);
  };

  const handlePrint = useReactToPrint({
    documentTitle: dashboardDoc?.name ?? 'dashboard',
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
    onPrintError: handleAfterPrint,
  });

  const reactToPrintContent = () => gridKpiRef.current;

  const handlePrintFunction = () => {
    handlePrint(reactToPrintContent);
  };

  const handleFullScreen = () => {
    handle.enter();
  };

  const getUserName = (uId) => (
    usersState?.users?.find((u) => u.id === uId)?.name || ''
  );

  /**
   * @async Fetch the values of the control variable
   * @param {object} database Database file id
   * @param {string} control Variable name
   * @param {string} dateType Date selector (only for date variable)
   * @returns {array} Value array options
   */
  const getControlValues = async (database, control, dateType, limits) => {
    const file = dashFiles.find((df) => df.file_id === database);
    if (!file) {
      return {
        fileError: [
          'Arquivo necessário não encontrado',
          'O arquivo responsável pela geração deste KPI foi excluído. Desta forma, não é possivel utilizar esta ferramenta.',
        ],
      };
    } if (!file.allowed) {
      return {
        fileError: [
          'Permissões insuficientes',
          `Você não possui permissão de leitura para o arquivo "${file.filename}".`,
          `Para utilizar esta ferramenta, solicite as permissões necessárias ao dono do arquivo: ${getUserName(file.owner)}`,
        ],
      };
    }
    const limitList = (
      limits ?? Object.values(globalFilterLimits).filter((gfl) => gfl.database === database)
    );
    const uniqueRes = await filesAPI.getUnique(database, control, dateType, limitList);
    if (uniqueRes.error) {
      setAlertConfig({ type: 'error', text: error.generic, child: uniqueRes.msg });
      return { opts: [] };
    } if (uniqueRes.res) {
      return { opts: uniqueRes.res.map((val) => ({ label: val, value: val, id: val })) };
    }
    return { opts: [] };
  };

  // const fetchDynamicKpi = async () => {
  //   const promises = Object.entries(dataToFilter.current).map(([fileId, Kpis]) => (
  //     kpiDataAPI.genMultData(Kpis, fileId)
  //   ));
  //   if (promises.length > 0) {
  //     await Promise.all(promises);
  //   }
  //   // await kpiDataAPI.genMultData(dataToFilter.current, kpiFile);
  //   setManualLoad(false);
  //   dataToFilter.current = {};
  // };
  const fetchDynamicKpi = async () => {
    const batch = dataToFilter.current;
    dataToFilter.current = {};

    try {
      const results = await Promise.all(
        Object.entries(batch).map(([fileId, kpis]) => (
          kpiDataAPI.genMultData(kpis, fileId)
        )),
      );

      const failure = results.find((result) => result?.error);

      if (failure) {
        setAlertConfig({
          type: 'error',
          text: failure.msg,
        });
      }
    } finally {
      if (Object.keys(dataToFilter.current).length === 0) {
        setManualLoad(false);
      }
    }
  };

  const addToFilter = (kpiId, kpiType, kpiBody, kpiFile, callback, extra = {}) => {
    clearTimeout(cron.current);
    dataToFilter.current[kpiFile] = {
      ...(dataToFilter.current[kpiFile] ?? {}),
      [kpiId]: {
        id: kpiId,
        type: kpiType,
        body: kpiBody,
        callback,
        extra,
      },
    };
    cron.current = setTimeout(() => {
      fetchDynamicKpi();
    }, 1000);
  };

  /** Request temporary kpi data */
  const handleDynamicKpi = (kpiId, kpiType, kpiBody, kpiFile, callback, extra = {}) => {
    if (collection === 'kpis') {
      setManualLoad(true);
      addToFilter(kpiId, kpiType, kpiBody, kpiFile, callback, extra);
    }
  };

  const lastReportFilters = useRef(new Map());

  const getReportGridFilters = (configuration) => {
    const sourceFilters = (
      globalFilters.current[configuration.fonteId] || {}
    );

    const global = globalFiltersOrder.current
      .map((id) => sourceFilters[id])
      .filter(Boolean)
      .map((filter) => ({
        column: filter.column,
        ftype: filter.ftype,
        values: filter.values,
        selector: filter.selector || '',
        inverse: filter.inverse ?? false,
      }));

    const local = (
      configuration.filtros
      ?? configuration.filters
      ?? []
    );

    return [...global, ...local];
  };

  const reportFilterSnapshot = JSON.stringify({
    collection,
    reports: Object.entries(reportDefinitions).map(([id, definition]) => ({
      id,
      version: definition.version,
      configuration: definition.configuration,
      filters: getReportGridFilters(definition.configuration),
    })),
  });

  const filterReportGrid = (id, definition, filters) => {
    handleDynamicKpi(
      id,
      'ReportGrid',
      {
        reportGrid: {
          version: definition.version,
          configuration: definition.configuration,
        },
        filters,
      },
      definition.configuration.fonteId,
      ({ dataset, error }) => {
        setReportDefinitions((previous) => {
          const current = previous[id];
          if (!current) return previous;

          return {
            ...previous,
            [id]: {
              ...current,
              ...(error ? {} : { dataset }),
              error: error || '',
              effectiveFilters: filters,
            },
          };
        });
      },
    );
  };

  useEffect(() => {
    const snapshot = JSON.parse(reportFilterSnapshot);

    if (snapshot.collection !== 'kpis') return;

    const activeIds = new Set();

    snapshot.reports.forEach((report) => {
      activeIds.add(report.id);

      const signature = JSON.stringify({
        configuration: report.configuration,
        filters: report.filters,
      });

      const previousSignature = lastReportFilters.current.get(report.id);

      if (previousSignature === signature) return;

      lastReportFilters.current.set(report.id, signature);

      const localFilters = (
        report.configuration.filtros
        ?? report.configuration.filters
        ?? []
      );

      // Na primeira abertura, sem filtros globais, a tabela já é
      // restaurada ou carregada pelo useReportDefinitions.
      if (
        previousSignature === undefined
        && JSON.stringify(report.filters) === JSON.stringify(localFilters)
      ) {
        return;
      }

      filterReportGrid(
        report.id,
        report,
        report.filters,
      );
    });

    lastReportFilters.current.forEach((_, id) => {
      if (!activeIds.has(id)) lastReportFilters.current.delete(id);
    });
  }, [reportFilterSnapshot]);

  const handleValueModifiers = async (fileId, kpiId, body) => {
    setManualLoad(true);
    const dtRes = await kpiDataAPI.genKpiData(body, fileId);
    if (dtRes.error) {
      setAlertConfig({
        type: 'error',
        text: dtRes.msg,
        // child: `${dtRes.raw.error}`,
      });
      setManualLoad(false);
      return false;
    }
    const dtSave = await gridKpiAPI.saveKpiValueFilters(
      kpiId, { ...body, error: dtRes.errorMsg?.split('|')[0] || null }, dtRes.data,
    );
    if (dtSave.error) {
      setAlertConfig({
        type: 'error',
        text: dtSave.msg,
        child: `${dtSave.raw}`,
      });
      setManualLoad(false);
      return false;
    }
    setManualLoad(false);
    return true;
  };

  const handleShowToolbar = () => {
    setShowToolbar(!showToolbar);
  };

  const getUsersShareds = () => {
    try {
      if (dashboardDoc) {
        const sharedIds = Object.keys(dashboardDoc.sharedWith || {});
        sharedIds.push(dashboardDoc.owner);
        const usersShareds = usersState.users.filter((u) => sharedIds.includes(u.id)
          || sharedIds.some((id) => u?.groups?.includes(id)));
        return usersShareds;
      }
      return [];
    } catch (err) {
      return [];
    }
  };

  const handleArchiveComment = (commentId) => {
    setAlertConfig({
      type: 'warning',
      text: 'Tem certeza que deseja remover este comentário?',
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: () => gridKpiAPI.archiveComment(commentId),
    });
  };

  const checkDependencyWritePermission = (file) => {
    if (file?.owner === user.id) return true;
    const sharedIds = [...(user.groups ?? []), user.id];
    for (let i = 0; i < sharedIds.length; i += 1) {
      if (file?.shared_write?.includes(sharedIds[i])) return true;
    }
    return false;
  };

  const handleDependencies = () => {
    /**
   * Opens an alert showing the file's update information
   * Attention with the logic of this function. A copy of that is used in FilesPage component
   * @param {object} file file info
   */
    const getReportStatus = async (file, f) => {
      const handleUpdate = async () => {
        const res = await filesAPI.baseUpdate(file.filename, file.file_id);
        if (res.error) {
          setAlertConfig({
            type: 'error',
            text: res.msg,
            child: res.raw,
          });
          return;
        }
        setAlertConfig({
          type: 'success',
          text: `Arquivo ${file.filename} atualizado com sucesso!`,
        });
        // gridKpiAPI.loadCardsFromCollection();
        loadDash();
        filesAPI.init();
        f();
      };

      const updateFile = () => {
        setAlertConfig({
          type: 'warning',
          text: `Atualizar o relatório ${file.filename}?`,
          withoutConfirm: true,
          withFunction: true,
          confirmFunction: () => handleUpdate(),
        });
      };

      const res = await filesAPI.getFile(file.file_id);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }

      const infoFile = {
        ...res.res,
      };
      const owner = usersState.users.find((u) => u.uid === infoFile.owner);

      let status = '';
      let msg = '';
      if (infoFile.lastAttemptStatus === 'ERROR') {
        status = 'error';
        msg = 'Erro ao atualizar o relatório!';
      } else if (infoFile.lastAttemptStatus === 'WARNING') {
        status = 'warning';
        msg = 'Relatório atualizado!';
      } else {
        status = 'success';
        msg = 'Relatório atualizado com sucesso!';
      }

      if (checkDependencyWritePermission(file)
        && ['GoogleDrive', 'Drive', 'OneDrive', 'PgSql', 'MySql', 'SqlServer', 'Benner', 'LegalOne', 'LegalOneExtractor', 'L1ReportsApi', 'Mail', 'JIRA', 'HighQ'].includes(file.origin)) {
        setAlertConfig({
          type: status,
          text: msg,
          child: <FileReport
            file={infoFile}
            owner={owner}
            adminAccess={false}
            manualUpdate={updateFile}
          />,
        });
      } else {
        setAlertConfig({
          type: status,
          text: msg,
          child: <FileReport
            file={infoFile}
            owner={owner}
            adminAccess={false}
          />,
        });
      }
    };

    const handleFile = async (toHandleFile, overwrite, overwriteName) => {
      let newFile = toHandleFile;
      if (overwriteName) {
        newFile = renameFile(newFile, overwriteName);
      }
      const {
        res: fileColumnsInfos,
        msg,
        error: handleError,
        raw,
      } = await filesAPI.uploadFile(newFile, overwrite);
      if (handleError) {
        setAlertConfig({ type: 'error', text: `${msg}`, child: raw });
        return null;
      }
      return { name: newFile.name, ...fileColumnsInfos };
    };

    const handleUpdateSubmit = async (newFile, oldFile, f) => {
      if (!newFile || !oldFile) return;
      const newFileName = (newFile.filename || newFile.name);

      const oldFileInfo = await filesAPI.getFileSample(oldFile.file_id, false, 10);
      if (oldFileInfo.error || !oldFileInfo.res) {
        setAlertConfig({
          type: 'error',
          text: oldFileInfo.msg,
          child: oldFileInfo.raw,
        });
        return;
      }

      newFile.types = oldFileInfo.res.types;

      const handleSubmit = async (startLine) => {
        const resUpdate = await filesAPI.baseUpdate(newFileName, oldFile.file_id, startLine);
        if (resUpdate.error) {
          setAlertConfig({
            type: 'error',
            text: resUpdate.msg,
            child: resUpdate.raw,
          });
          return;
        }
        setAlertConfig({
          type: 'success',
          text: 'Arquivo atualizado com sucesso',
          child: resUpdate.res.message,
        });
        // gridKpiAPI.loadCardsFromCollection();
        loadDash();
        filesAPI.init();
        f();
      };

      setModalConfig({
        title: 'Atualização de arquivo',
        className: 'updateFileModal',
        confirmClassName: 'submit_update_file',
        children: (
          <FileVisualize
            fileData={newFile}
            reopenFile={filesAPI.reopen}
            fileName={newFileName}
            handleSubmit={handleSubmit}
            hide={closeModal}
          />
        ),
      });
    };

    /**
    * Opens an modal to upload a new file to be updated
    * Attention with the logic of this function. A copy of that is used in FilesPage component
    * @param {object} file file info
    */
    const handleUpdateFile = async (file, f) => {
      const handleUpdateByFile = async (newFile) => (
        handleUpdateSubmit(await handleFile(newFile, true), file, f)
      );

      setModalConfig({
        title: 'Atualização de arquivo',
        close: closeModal,
        className: 'updateFileModal',
        children: (
          <FileUpload
            availableSize={(totalSize - filesState.size) * 1024 ** 2}
            uploadFile={handleUpdateByFile}
            updateMode
            updatingFileName={file.filename.split('.metrics')[0]}
            hide={closeModal}
            // tourContext={{ tourOpen, nextStep }}
          />
        ),
      });
    };

    setModalConfig({
      title: 'Lista de dependências',
      yesLabel: 'Ok',
      children: (
        <DashboardDependencies
          currentUser={user}
          dashboard={dashboardDoc}
          files={dashFiles}
          users={usersState.users}
          numberOfKpis={kpiItemList.length}
          close={closeModal}
          deviceType={device}
          getReportStatus={getReportStatus}
          updateFile={handleUpdateFile}
          userHasWritePermission={checkDependencyWritePermission}
        />
      ),
    });
  };

  const getFileColumns = async (database) => {
    const res = await filesAPI.getColumns(database);
    if (res.error) {
      setAlertConfig({ type: 'error', text: error.generic, child: res.msg });
      return [];
    } if (res.columns) {
      return Object.keys(res.columns).map((val) => ({
        label: val, value: val, id: val, type: getType(res.columns[val]),
      })) ?? [];
    }
    return [];
  };

  const getDependencyColumns = async (database) => {
    const opts = await getFileColumns(database);
    setDashFiles((files) => files.map((f) => {
      if (f.file_id === database) f.columns = opts;
      return f;
    }));
    return opts;
  };

  const handleGlobalFilterSelectors = async (selectedGFilters, selectedGLimits) => {
    const result = await gridKpiAPI.setGlobalFilterSelectors(selectedGFilters, selectedGLimits);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }
    setAlertConfig({
      type: 'success',
      text: 'Colunas de filtro global editadas com sucesso!',
    });
  };

  const filterColumnsModal = (toolbarStateSetter) => {
    const getColumnUnique = async (database, column, selector) => {
      const res = await filesAPI.getUnique(database, column, selector);
      if (res.error) {
        setAlertConfig({ type: 'error', text: error.generic, child: res.msg });
        return [];
      }
      return res.res.map((val) => ({ label: val, value: val, id: val }));
    };

    setModalConfig({
      title: 'Filtro global',
      children: (
        <GlobalFiltersColumns
          usedFiles={dashFiles}
          getFileColumns={getDependencyColumns}
          getUnique={getColumnUnique}
          hide={closeModal}
          globalFiltersState={[globalFilterSelectors, setGlobalFilterSelectors]}
          globalFilterLimitsState={[globalFilterLimits, setGlobalFilterLimits]}
          showToolbar={toolbarStateSetter}
          submit={handleGlobalFilterSelectors}
        />
      ),
    });
  };

  const checkAccessToDependency = (kpi) => {
    const dependency = dashFiles.find((file) => file.file_id === kpi.database);
    return dependency;
  };

  const checkUserDependencyPermission = (kpi) => {
    const dependency = dashFiles.find((file) => file.file_id === kpi.database);
    if (dependency?.owner === currentUser.uid) return true;
    const sharedIds = [...(user.groups ?? []), currentUser.uid];
    for (let i = 0; i < sharedIds.length; i += 1) {
      if (
        dependency?.shared_write?.includes(sharedIds[i])
        || dependency?.shared_read?.includes(sharedIds[i])
      ) return true;
    }
    return false;
  };

  const getKpiWithoutDependencyMessage = (dependency, kpi) => {
    const originalFile = filesState.instanceFiles.find((file) => file.file_id === kpi.database);
    if (!originalFile) {
      setAlertConfig({
        type: 'error',
        text: 'Dependência não existe!',
        child: (
          <div style={{ fontSize: '14px' }}>
            O arquivo utilizado na construção deste KPI foi apagado.
            Portanto, este KPI não pode ser atualizado!
          </div>
        ),
      });
      return;
    }
    const currentOwner = usersState.users.find((u) => u.uid === originalFile.owner);
    setAlertConfig({
      type: 'warning',
      text: 'Você não possui acesso ao arquivo de origem!',
      child: (
        <>
          <div style={{ fontSize: '14px' }}>
            Você não possui permissão de acesso ao arquivo
            {' '}
            <b style={{ color: theme.warning }}>{originalFile.filename}</b>
            {' '}
            de propriedade do(a) usuário(a)
            {' '}
            <b style={{ color: theme.warning }}>{currentOwner.name || currentOwner.email.split('@')[0]}</b>
            .
          </div>
          <div style={{ fontSize: '14px' }}>
            Para atualizar este KPI, solicite o compartilhamento do arquivo
            {' '}
            <b style={{ color: theme.warning }}>{originalFile.filename}</b>
            {' '}
            ao proprietário.
          </div>
        </>
      ),
    });
  };

  const handleExportDashboard = async () => {
    const result = await gridKpiAPI.exportDashboard(dashboardDoc.id, dashboardDoc?.name || 'Dashboard');
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }
    setAlertConfig({
      type: 'success',
      text: 'Dashboard exportado com sucesso!',
    });
  };

  const handleIframe = (item) => {
    const submitIframe = async (editedItem) => {
      const LabelObject = {
        name: '',
        link: editedItem.link,
        linkType: editedItem.linkType,
        status: 'active',
        type: 'Iframe',
        style: {},
      };
      const result = await gridKpiAPI.saveInsightCard(
        kpisLayout, editedItem.id, LabelObject, '',
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: `Erro ao tentar ${item ? 'editar' : 'adicionar'} o conteúdo externo`,
        });
        return false;
      }
      // gridKpiAPI.loadCardsFromCollection();
      loadDash();
      setAlertConfig({
        type: 'success',
        text: `Conteúdo externo ${item ? 'editado' : 'adicionado'} com sucesso!`,
      });
      return true;
    };

    const handlePlotlyLink = async (plotlyId) => {
      const plotlyRes = await gridKpiAPI.getPlotlyLink(plotlyId);
      if (plotlyRes.error) return null;
      return plotlyRes.link;
    };

    const plan = userCompany?.plan || 'advanced';
    const companyPlan = plans[plan];
    const externalKpis = userCompany?.externalKpis ?? companyPlan.externalKpis;
    if (!externalKpis) {
      setAlertConfig({
        type: 'warning',
        text: 'Permissões insuficientes',
        child: 'Seu plano não permite adicionar conteúdos externos. Para alterar o plano, entre em contato com o suporte.',
      });
      return;
    }
    setModalConfig({
      title: `${item ? 'Editar' : 'Adicionar'} Outros Conteúdos`,
      className: 'modal_iframe',
      children: (
        <IframeCreate
          item={item}
          hide={closeModal}
          submit={submitIframe}
          stylesData={dashboardDoc.cardStyles}
          getPlotlyLink={handlePlotlyLink}
        />
      ),
    });
  };

  const handleSubsetTable = (item) => {
    const hideSubsetTableCreate = () => closeModal('subsettable-create');

    const filesOpts = filesState.files?.map((val) => ({
      label: val.filename.replace('.metrics', ''),
      value: val.file_id,
      id: val.file_id || val.filename,
      owner: val.owner,
    })) ?? [];

    const genTable = async (metaInfo, fileId, page, sortBy, sortOrder) => {
      const res = await kpiDataAPI.genSubsetData(metaInfo, fileId, page, sortBy, sortOrder);
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: 'Algo está incorreto!',
          child: 'Os elementos selecionados não permitem a criação da Tabela',
        });
        return null;
      }
      return res;
    };

    const getColumnUnique = async (fileId, column, dFormat) => {
      const dateFormat = (dFormat?.value || dFormat) ?? '';
      const uniqueRes = await filesAPI.getUnique(fileId, column.value ?? column, dateFormat);
      if (uniqueRes.error) {
        setAlertConfig({ type: 'error', text: error.generic, child: uniqueRes.msg });
        return [];
      }
      return uniqueRes.res.map((val) => ({ label: val, value: val, id: val }));
    };

    const submitTable = async (title, newItem, newStyleConfig) => {
      const CHARTTYPE = 'SubsetTable';
      const rawSC = {
        ...newStyleConfig,
        TitleControl: title,
        type: CHARTTYPE,
      };
      const defaultSC = getDefaultStyle(CHARTTYPE);
      /** List of keys to keep */
      const alwaysKeep = [
        'GlobalValue', 'ShowTitleControl', 'StyleTitleControl', 'type', 'TitleControl', 'TotalFunctionControl',
      ];
      // Remove attributes that have not changed from the default
      const tmp = Object.entries(rawSC).reduce((aux, [k, v]) => {
        if (alwaysKeep.includes(k)) aux[k] = v;
        else if (defaultSC[k] !== undefined && !isEqual(v, defaultSC[k])) aux[k] = v;
        return aux;
      }, {});

      try {
        const metaGridInfo = newItem.id !== 'new' ? (
          kpisLayout?.[gridBreakpoint]?.find((l) => l.i === newItem.id) ?? {}
        ) : {};
        if (!item && subsetTableReachedLimit()) {
          const subsetTableLimit = (
            userCompany?.subsetTableLimit
            ?? plans?.[userCompany?.plan || 'advanced']?.subsetTableLimit
          );
          setAlertConfig({
            type: 'warning',
            text: 'Limite de Subtabelas atingido!',
            child: `Seu plano permite ${subsetTableLimit} Subtabela${subsetTableLimit > 1 ? 's' : ''} por Dashboard. Para alterar o plano, entre em contato com o suporte.`,
          });
          return;
        }
        const res = await gridKpiAPI.saveKpiCard(
          title, newItem.id, CHARTTYPE, kpisLayout, newItem.meta, metaGridInfo, {}, tmp,
        );
        if (res.error) {
          setAlertConfig({
            type: 'error',
            text: res.msg,
          });
          return;
        }
        hideSubsetTableCreate();
        // gridKpiAPI.loadCardsFromCollection();
        loadDash();
        setAlertConfig({
          type: 'success',
          text: `Subtabela ${item ? 'editada' : 'adicionada'} com sucesso!`,
        });
      } catch (er) {
        console.log(er);
      }
    };

    const haveTitleModal = (title, newItem, newStyleConfig) => {
      setModalConfig({
        title: 'Salvar Subtabela',
        notLabel: 'Cancelar',
        yesLabel: 'Salvar',
        yesFunc: () => submitTable(title, newItem, newStyleConfig),
        confirmClassName: 'submit_name_btn',
        children: (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
          }}
          >
            <span style={{ fontSize: '1.2rem', padding: '10px 0px' }}>
              Tudo pronto para salvar a Subtabela:
              {' '}
              <b>
                {title}
              </b>
            </span>
            <span>Tem certeza que deseja continuar?</span>
          </div>
        ),
      });
    };

    const notHaveTitleModal = (newItem, newStyleConfig) => {
      const onSubmit = (newTitle) => {
        haveTitleModal(newTitle, newItem, newStyleConfig);
      };

      setModalConfig({
        title: 'Salvar Subtabela',
        className: 'title_modal',
        children: (
          <TitleModal submit={onSubmit} />
        ),
      });
    };

    const handleSubmitTable = (newItem, newStyleConfig) => {
      if (newStyleConfig.TitleControl) {
        haveTitleModal(newStyleConfig.TitleControl, newItem, newStyleConfig);
      } else notHaveTitleModal(newItem, newStyleConfig);
    };

    setModalConfig({
      title: `${item ? 'Editar' : 'Adicionar'} Subtabela`,
      className: 'modal_iframe',
      disableFocus: true,
      nodeTarget: 'subsettable-create',
      children: (
        <SubsetTableCreate
          item={item}
          hide={hideSubsetTableCreate}
          submit={handleSubmitTable}
          stylesData={dashboardDoc.cardStyles}
          filesOpts={filesOpts}
          getFileColumns={getFileColumns}
          genTable={genTable}
          userId={currentUser.uid}
          getColumnUnique={getColumnUnique}
        />
      ),
    });
  };

  const reportColumnsCache = useRef(new Map());
  const carregarCamposReportGrid = useCallback(async (fileId, { force = false } = {}) => {
    const key = JSON.stringify([currentUser.uid, currentUser.companyId, fileId]);
    const cache = reportColumnsCache.current;
    if (!force && cache.has(key)) return cache.get(key);

    const request = Promise.resolve().then(async () => {
      const result = await filesAPI.getColumns(fileId);
      if (result.error) {
        throw new Error(result.msg || 'Não foi possível carregar os campos.');
      }
      return Object.entries(result.columns || {}).map(([column, type]) => ({
        key: column,
        label: column,
        tipo: type === 'float' ? 'numero' : type,
        type,
      }));
    });
    cache.set(key, request);

    try {
      return await request;
    } catch (failure) {
      if (cache.get(key) === request) cache.delete(key);
      throw failure;
    }
  }, [filesAPI, currentUser.uid, currentUser.companyId]);

  const carregarValoresReportGrid = useCallback(async (fileId, column, selector) => {
    const result = await filesAPI.getUnique(fileId, column, selector);
    if (result.error) throw new Error(result.msg || 'Não foi possível carregar os valores do campo.');
    return (result.res || []).map((value) => ({
      id: String(value), label: String(value), value,
    }));
  }, [filesAPI]);

  const handlePivotExplorer = async () => {
    const result = await gridKpiAPI.saveInsightCard(
      kpisLayout,
      'new',
      {
        name: 'Explorador',
        status: 'active',
        type: 'ReportGrid',
        meta: {},
        database: '',
        fileOwner: '',
        style: {},
      },
      '',
    );

    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
      });
      return;
    }

    loadDash();
  };

  const gerarDataset = async (configuration, signal) => {
    const result = await kpiDataAPI.genReportGridData(
      configuration,
      signal,
    );

    if (result.error) {
      throw new Error(result.msg);
    }

    return result.data;
  };

  const saveReportGridDefinition = async (item, definition, signal) => {
    if (!(isOwner || user.role === 'super')) {
      throw new Error('Sem permissão para editar este explorador.');
    }

    if (signal?.aborted) return;

    const fileId = definition.configuration.fonteId;
    const file = filesState.files.find(
      (source) => source.file_id === fileId,
    );

    if (!file) {
      throw new Error('A fonte selecionada não está disponível.');
    }

    const meta = {
      ...item.meta,
      database: fileId,
      fileOwner: file.owner,
      reportGrid: {
        version: definition.version,
        configuration: definition.configuration,
      },
    };

    if (!definition.configuration.saveConfigurationOnly) {
      const bytes = new Blob([JSON.stringify({
        meta: JSON.stringify(meta),
        data: JSON.stringify(definition.dataset),
        style: JSON.stringify(definition.settings),
      })]).size;
      if (bytes > 900 * 1024) {
        throw new Error('A tabela é grande demais para salvar no documento. Marque "Salvar apenas as configurações" na aba Dados e aplique novamente.');
      }
    }

    const layoutItem = kpisLayout?.[gridBreakpoint]?.find(
      (entry) => entry.i === item.id,
    ) || {};

    const result = await gridKpiAPI.saveKpiCard(
      definition.settings.title.text || item.name || 'Explorador',
      item.id,
      'ReportGrid',
      kpisLayout,
      meta,
      layoutItem,
      definition.configuration.saveConfigurationOnly ? null : definition.dataset,
      definition.settings,
    );

    if (result.error) {
      throw new Error(result.msg || 'Não foi possível salvar o explorador.');
    }
  };

  const handleReportGridSettings = (item) => {
    if (!(isOwner || user.role === 'super')) return;

    let deleting = false;
    const deleteExplorer = () => {
      setAlertConfig({
        type: 'warning',
        text: `Apagar o explorador "${item.name || 'Explorador'}"?`,
        child: 'O card e suas configurações serão removidos. O arquivo de origem será mantido.',
        withoutConfirm: true,
        withFunction: true,
        confirmFunction: async () => {
          if (deleting) return;
          deleting = true;
          try {
            const result = await gridKpiAPI.removeCards([item], kpiItemList, kpisLayout);
            if (result.error) throw new Error(result.msg || 'Não foi possível apagar o explorador.');
            setReportDefinitions((previous) => {
              const next = { ...previous };
              delete next[item.id];
              return next;
            });
            closeModal();
            setAlertConfig({ type: 'success', text: 'Explorador removido com sucesso!' });
          } catch (failure) {
            setAlertConfig({ type: 'error', text: failure.message || 'Não foi possível apagar o explorador.' });
          } finally {
            deleting = false;
          }
        },
      });
    };

    const fontes = (filesState.files || []).map((file) => ({
      id: file.file_id,
      nome: file.filename.replace(/\.metrics$/, ''),
    }));

    setModalConfig({
      title: 'Configurações do relatório',
      children: (
        <ReportGridEditor
          key={item.id}
          // initialValue={reportDefinitions[item.id]}
          initialValue={
            reportDefinitions[item.id]
              ? {
                ...reportDefinitions[item.id],
                dataset: reportDefinitions[item.id].effectiveFilters !== undefined
                  ? undefined
                  : reportDefinitions[item.id].dataset,
              }
              : undefined
          }
          createConfiguration={{
            fonteId: '',
            valor: '',
            linhas: [],
            colunas: [],
            filtros: [],
            openingBalance: '',
            saveConfigurationOnly: false,
          }}
          renderDataControls={(configuration, onChange) => (
            <FileConfigForm
              value={configuration}
              onChange={onChange}
              fontes={fontes}
              carregarCampos={carregarCamposReportGrid}
              onDelete={deleteExplorer}
            />
          )}
          renderFilterControls={(configuration, onChange, disabled) => (
            <ReportGridFilters
              value={configuration}
              onChange={onChange}
              carregarCampos={carregarCamposReportGrid}
              carregarValores={carregarValoresReportGrid}
              disabled={disabled}
            />
          )}
          canApply={(configuration) => Boolean(
            configuration.fonteId
            && configuration.valor
            && configuration.linhas.length
            && configuration.colunas.length,
          )}
          onGenerate={gerarDataset}
          onCancel={closeModal}
          onApply={(definition) => {
            setReportDefinitions((previous) => ({
              ...previous,
              [item.id]: definition,
            }));

            closeModal();
          }}
          onSave={(definition, signal) => (
            saveReportGridDefinition(item, definition, signal)
          )}
        />
      ),
    });
  };

  const handleReportGridDetails = (item, cell) => {
    const definition = reportDefinitions[item.id];
    if (!definition) return;

    const payload = {
      ...definition.configuration,
      filtros: (
        definition.effectiveFilters
        ?? definition.configuration.filtros
        ?? definition.configuration.filters
        ?? []
      ),
      rowFilters: cell.rowFilters,
      columnFilters: cell.columnFilters,
    };

    // Evita enviar os dois aliases com listas diferentes.
    delete payload.filters;

    setModalConfig({
      title: 'Detalhes dos lançamentos',
      children: (
        <ReportGridDetails
          key={`${item.id}:${JSON.stringify([
            cell.rowFilters,
            cell.columnFilters,
          ])}`}
          payload={payload}
          loadDetails={kpiDataAPI.getReportGridDetails}
        />
      ),
    });
  };

  const handleSize = () => {
    const changeSize = async (sz) => {
      if (isOwner) await handleSaveLayout({ ...kpisLayout, size: sz });
      setGridSize(sz);
      setGridSizeLocal(sz);
      closeModal();
    };

    setModalConfig({
      title: 'Largura do Grid',
      className: 'modal_grid_size',
      children: (
        <DashGridSize
          savedSize={gridSize}
          close={closeModal}
          submit={changeSize}
        />
      ),
    });
  };

  const getFileSample = async (fileId) => {
    const sampleRes = await filesAPI.getFileSample(fileId, false);
    if (sampleRes.error) {
      setAlertConfig({
        type: 'error',
        text: sampleRes.msg,
        child: sampleRes.raw,
      });
      return null;
    }
    return sampleRes.res;
  };

  const warningSelectFile = (callback) => {
    setAlertConfig({
      type: 'warning',
      child: 'Deseja mesmo alterar?',
      text: 'Kpi\'s adicionados serão removidos',
      withFunction: true,
      withoutConfirm: true,
      confirmFunction: callback,
    });
  };

  const handleQuickDash = () => {
    const filesOpts = filesState.files?.map((val) => ({
      label: val.filename.replace('.metrics', ''),
      value: val.file_id,
      id: val.file_id || val.filename,
      owner: val.owner,
    })) ?? [];

    const handleCloseMainModal = () => {
      setShowQuickDash(false);
      closeModal();
    };

    const handleAssistantCall = async (fileId, prompt) => {
      const res = await filesAPI.callAIDashboard(fileId, prompt);
      if (res.error && res.type === 'error') {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return null;
      }
      const data = JSON.parse(res.message);
      if (data && data.length > 0) return data;
      setAlertConfig({
        type: 'warning',
        text: 'Sem resposta do assistante',
        child: 'O assistente não conseguiu gerar KPI\'s válidos. Reformule sua pergunta e tente novamente.',
      });
      return null;
    };

    const submitKpis = async (fileId, suggestions) => {
      const res = await gridKpiAPI.genAssistantKpis(
        fileId, dashboardDoc.id, suggestions,
      );
      if (res.error) {
        setAlertConfig({
          type: 'error',
          text: res.msg,
          child: res.raw,
        });
        return;
      }
      // gridKpiAPI.loadCardsFromCollection();
      loadDash();
      setAlertConfig({
        type: 'success',
        text: 'KPI\'s criados com sucesso!',
      });
      handleCloseMainModal();
    };

    setModalConfig({
      title: 'KPIs Rápidos',
      className: 'modal_quickdash',
      disableFocus: true,
      closeFunc: handleCloseMainModal,
      children: (
        <QuickDash
          allowGpt={userCompany?.gptApi && userCompany?.gptAgents?.includes('quickDash')}
          gptTokens={{
            used: gptUsedTokens,
            limit: userCompany.gptTokens ?? 100,
            bonus: gptBonusTokens,
          }}
          closeModal={closeModal}
          hide={handleCloseMainModal}
          submit={submitKpis}
          filesOpts={filesOpts}
          setModalConfig={setModalConfig}
          warningSelectFile={warningSelectFile}
          getFileSample={getFileSample}
          virtualAssistant={handleAssistantCall}
          blockGeneration={!(user.agents?.quickDash ?? true)}
        />
      ),
    });
  };

  useEffect(() => {
    if (showQuickDash) handleQuickDash();
  }, [showQuickDash, gptUsedTokens, gptBonusTokens]);

  const setShowQuickDashTrue = () => setShowQuickDash(true);

  const resumeApiCallCheck = async (call, ...params) => {
    const res = await call(...params);
    if (res.error) {
      setAlertConfig({
        type: 'error',
        text: res.msg,
        child: res.raw,
      });
      return null;
    }
    return res;
  };

  const resumeKpiView = async (timestamp, resume) => {
    let kpis = gridKpiState.resumeKpis[timestamp];
    if (!(kpis && kpis[resume.id])) {
      kpis = await resumeApiCallCheck(gridKpiAPI.getKpisFromResume, timestamp);
    }
    setModalConfig({
      title: '',
      transparent: true,
      children: (
        <ResumeKpiView
          close={closeModal}
          resume={resume.response}
          timestamp={new Date(timestamp)}
          kpi={kpis?.[resume.id]}
          cardStyles={dashboardDoc?.cardStyles}
        />
      ),
    });
  };

  const handleMultiSelection = () => setMultiSelection((old) => {
    if (old) setSelectedCards(new Map());
    return !old;
  });

  const toggleSelected = (item) => {
    setSelectedCards((selected) => {
      const aux = new Map(selected);
      if (!aux.delete(item.id)) aux.set(item.id, item);
      return aux;
    });
  };

  const toggleAllSelected = () => {
    setSelectedCards((selected) => {
      const allCards = kpiItemList?.map((k) => [k.id, k]) ?? [];
      return new Map(selected.size === allCards.length ? [] : allCards);
    });
  };

  const handleTransferSelected = () => {
    const handleSubmit = async (targetDashs) => {
      const result = await gridKpiAPI.transferKpis(
        [...selectedCards.values()],
        targetDashs,
        kpisLayout,
        (
          userCompany?.subsetTableLimit
          ?? plans?.[userCompany?.plan || 'advanced']?.subsetTableLimit
        ),
        userCompany?.plan ?? 'advanced',
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
          okFunction: closeModal,
        });
        return;
      }
      const s = (prefix = '') => (selectedCards.size > 1 ? `${prefix}s` : '');
      let status = 'success';
      let mainMessage = `Transferência da${s()} cópia${s()} do${s()} KPI${s("'")} realizada com sucesso!`;
      if ((result.errors.length + result.fileErrors.length) > 0) {
        if (result.updated.length > 0) {
          status = 'warning';
          mainMessage = `Transferência da${s()} cópia${s()} do${s()} KPI${s("'")} realizada parcialmente!`;
        } else {
          status = 'error';
          mainMessage = `Não foi possível realizar a transferência da${s()} cópia${s()} do${s()} KPI${s("'")}`;
        }
      }
      setAlertConfig({
        type: status,
        text: mainMessage,
        child: (
          <ResponseList
            updatedDashs={result.updated}
            errorDashs={result.errors}
            errorFiles={result.fileErrors}
            dashboards={dashboards}
            fileList={filesState.files}
          />
        ),
        okFunction: closeModal,
      });
    };

    const options = dashboards.filter((dashboard) => (
      dashboard.owner === currentUser.uid
      && dashboard.id !== dashboardDoc.id
      && !dashboard?.isDeleted
    )).map((dashboard) => (
      {
        ...dashboard,
        label: dashboard.name,
        value: dashboard.id,
      }
    ));

    setModalConfig({
      title: 'Transferir Cópias',
      children: (
        <TransferKpi
          currentKpis={[...selectedCards.values()]}
          dashOptions={options}
          close={closeModal}
          submit={handleSubmit}
        />
      ),
    });
  };

  const handleDeleteSelected = () => {
    const verifyDelete = async () => {
      const result = await gridKpiAPI.removeCards(
        [...selectedCards.values()], kpiItemList, kpisLayout,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }
      setAlertConfig({
        type: 'success',
        text: `${selectedCards.size} ${selectedCards.size > 1 ? "KPI's removidos" : 'KPI removido'} com sucesso!`,
      });
      handleMultiSelection();
    };

    setAlertConfig({
      type: 'warning',
      text: "Tem certeza que deseja remover os KPI's selecionados?",
      child: `${selectedCards.size} ${selectedCards.size > 1 ? "KPI's serão removidos " : 'KPI será removido '}
      e não poderâo ser restaurados.`,
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: verifyDelete,
    });
  };

  return (
    <div>
      {(isLoading || gridAPILoading || manualLoad || filesState.isLoading) && (
        <Loader underText={manualLoad === 'print' ? printLoadMsg : ''} />
      )}
      <GridKpisToolbar
        isOwner={isOwner}
        isAdmin={claimsUser?.is_admin}
        isGuest={user.role === 'guest'}
        dashboardKeyValid={dashboardKeyValid}
        gridKpiState={gridKpiState}
        gridKpiAPI={gridKpiAPI}
        editPainel={editPainel}
        setEditPainel={setEditPainel}
        goBack={() => history.push('/home')}
        openMural={() => setOpenMural(true)}
        multiSelection={multiSelection}
        selectedCards={selectedCards}
        toggleAllSelected={toggleAllSelected}
        setMultiSelection={handleMultiSelection}
        transferSelected={handleTransferSelected}
        deleteSelected={handleDeleteSelected}
        openVSDrawer={toggleVSDrawer}
        allowGpt={userCompany?.gptApi && userCompany?.gptAgents?.includes('dashSummary')}
        openSaveAsTemplate={handleCreateTemplate}
        // openShare={() => setOpenShare(true)}
        openShare={handleShare}
        handleSize={handleSize}
        isSizeFixed={gridSize && gridSize !== 'auto'}
        // snaps
        handleCreateSnapshot={handleCreateSnapshot}
        handleEditSnapshot={handleEditSnapshot}
        handleDeleteSnapshot={handleDeleteSnapshot}
        changeCurrentSnap={changeCurrentSnap}
        handleScheduleSnapshot={handleScheduleSnapshot}
        //
        redirectToInsightCreation={redirectToInsightCreation}
        redirectToKpiCreation={redirectToKpiCreation}
        // card Styles
        handleCardStyles={handleCardStyles}
        //
        printFunction={handlePrintFunction}
        gridBreakpoint={gridBreakpoint}
        showToolbar={showToolbar}
        handleShowToolbar={handleShowToolbar}
        getDependencies={handleDependencies}
        handleFullScreen={handleFullScreen}
        handleExportDashboard={handleExportDashboard}
        // Global Filters
        fileDependencies={dashFiles}
        globalFilters={globalFilters}
        globalFiltersOrder={globalFiltersOrder}
        globalFilterSelectors={globalFilterSelectors}
        setGlobalFilterSelectors={setGlobalFilterSelectors}
        getColumnValues={getControlValues}
        filterColumnsModal={filterColumnsModal}
        //
        handleIframe={handleIframe}
        handleSubsetTable={handleSubsetTable}
        handleQuickDash={setShowQuickDashTrue}
        handlePivotExplorer={handlePivotExplorer}
      />
      {kpiItemList.length > 0 ? (
        <GridKpis
          gridKpiRef={setGridRef}
          isOwner={isOwner}
          isSuper={['super', 'support'].includes(user.role)}
          isGuest={user.role === 'guest'}
          dashboardFileDependencies={dashFiles}
          editPainel={editPainel}
          multiSelection={multiSelection}
          selectedCards={selectedCards}
          toggleSelected={toggleSelected}
          gridSize={gridSize}
          showToPrint={showToPrint}
          dashboardKey={dashboardKeyValid}
          saveLayout={handleSaveLayout}
          //
          kpiItemList={kpiItemList}
          dashboardDoc={dashboardDoc}
          kpisLayout={kpisLayout}
          //
          gridKpiAPI={gridKpiAPI}
          gridKpiState={gridKpiState}
          dashboards={dashboards.filter((dashboard) => (
            dashboard.owner === currentUser.uid
            && dashboard.id !== dashboardDoc.id
            && !dashboard?.isDeleted
          ))}
          gridBreakpoint={gridBreakpoint}
          setGridBreakpoint={setGridBreakpoint}
          showToolbar={showToolbar}
          //
          getControlValues={getControlValues}
          handleDynamicKpi={handleDynamicKpi}
          checkAccessToDependency={checkAccessToDependency}
          checkUserPermission={checkUserDependencyPermission}
          getKpiWithoutDependencyMessage={getKpiWithoutDependencyMessage}
          fullScreenHandler={handle}
          handleIframe={handleIframe}
          handleSubsetTable={handleSubsetTable}
          handleValueModifiers={handleValueModifiers}
          //
          globalFilters={globalFilters}
          globalFiltersOrder={globalFiltersOrder}
          getUserName={getUserName}
          subsetTableReachedLimit={subsetTableReachedLimit}
          subsetTableLimit={(
            userCompany?.subsetTableLimit
            ?? plans?.[userCompany?.plan || 'advanced']?.subsetTableLimit
          )}
          plan={userCompany?.plan ?? 'advanced'}
          //
          reportDefinitions={reportDefinitions}
          onReportRetry={retryReportGrid}
          onReportDensityChange={handleReportDensityChange}
          handleReportGridSettings={handleReportGridSettings}
          handleReportGridDetails={handleReportGridDetails}
        />
      ) : !isLoading && (
        <NoKpi
          dashboardKey={dashboardKeyValid}
          isShared={userId !== currentUser.uid}
          role={user.role}
        />
      )}
      <Mural
        open={openMural}
        close={closeMural}
        users={getUsersShareds()}
        currentUser={currentUser}
        kpiState={gridKpiState}
        // loaders
        loadComments={gridKpiAPI.loadComments}
        loadMoreComments={gridKpiAPI.loadMoreComments}
        // actions
        addComment={gridKpiAPI.addComment}
        editComment={gridKpiAPI.editComment}
        archiveComment={handleArchiveComment}
      />
      <VSResumeDrawer
        isOpen={VSDrawer}
        close={closeVSDrawer}
        kpis={kpiItemList}
        gptTokens={{
          used: gptUsedTokens,
          limit: userCompany.gptTokens ?? 100,
          bonus: gptBonusTokens,
        }}
        dashResumes={gridKpiState.resumes}
        loadDashResumes={
          (...params) => resumeApiCallCheck(gridKpiAPI.getDashResumes, ...params)
        }
        loadKpisResumes={
          (...params) => resumeApiCallCheck(gridKpiAPI.getKpisResumes, ...params)
        }
        genDashKpisResumes={
          (...params) => resumeApiCallCheck(gridKpiAPI.genDashKpisResumes, ...params)
        }
        setListener={
          (...params) => resumeApiCallCheck(gridKpiAPI.setResumesListener, ...params)
        }
        showKpiFromResume={resumeKpiView}
        hideGeneration={!isOwner}
        blockGeneration={!(user.agents?.dashSummary ?? true)}
      />
    </div>
  );
}
